import { Download } from '@lifeomic/chromicons';
import { Tooltip } from '@lifeomic/chroma-react/components/Tooltip';
import {
  IconButton,
  IconButtonProps,
} from '@lifeomic/chroma-react/components/IconButton';
import * as React from 'react';
import { QueryObserverResult } from '@tanstack/react-query';

export const generateLink = ({
  downloadUrl,
  downloadTitle,
}: {
  downloadUrl: string;
  downloadTitle?: string;
}) => {
  const a = document.createElement('a');
  a.href = downloadUrl;
  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  a.setAttribute('download', downloadTitle);
  a.click();
  a.remove();
};

export interface FetchResponse {
  name?: string;
  downloadUrl: string;
}

export interface FileDownloadButtonProps
  extends Pick<IconButtonProps, 'aria-label' | 'color' | 'disabled'> {
  /**
   * @description Should the button call `e.currentTarget.blur()`?
   */
  blurTargetOnClick?: boolean;
  /**
   * @description The file name provided to the browser
   */
  downloadTitle: string;
  /**
   * @description The `onClick` should make a request for some sort of
   * object in the shape of Promise<FetchResponse>.
   */
  onClick?: () => Promise<QueryObserverResult<FetchResponse>>;
  /**
   * @description Title for the tooltip (optional); however,
   * required when `withTooltip` is provided
   */
  tooltipTitle?: string;
  /**
   * @description Should the component be wrapped in a Tooltip?
   */
  withTooltip?: boolean;
}

/**
 * @description A component for downloading a file to the browser
 * by creating a `<a />` element, clicking it, and then removing it.
 *
 * Can be conditionally wrapped in a Tooltip.
 */
export const FileDownloadButton: React.FC<FileDownloadButtonProps> = ({
  blurTargetOnClick = true,
  downloadTitle,
  onClick,
  tooltipTitle,
  withTooltip,
  ...rootProps
}) => {
  const button = (
    <IconButton
      icon={Download}
      onClick={async e => {
        e.stopPropagation();

        if (blurTargetOnClick) {
          e.currentTarget.blur();
        }

        const response = await onClick?.();
        if (response?.data?.downloadUrl)
          generateLink({
            downloadTitle: downloadTitle || response?.data?.name,
            downloadUrl: response?.data?.downloadUrl,
          });
      }}
      {...rootProps}
    />
  );

  if (!withTooltip) {
    return button;
  }

  if (!tooltipTitle) {
    throw new Error(
      'Component FileDownloadButton `withTooltip` requires `tooltipTitle`',
    );
  }

  return <Tooltip title={tooltipTitle}>{button}</Tooltip>;
};
